/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef, useState } from 'react';
import { Check, FileText, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  PPP_FIELDS,
  bridgeLines,
  pppForm,
  pppPatch,
  pppReadFailure,
  pppStatusText,
  runsInProgressText,
  validatePpp,
  type PppAdminView,
  type PppField,
  type PppForm,
} from '../lib/ppp';
import { Button, Checkbox, Field, Icon, Input, NumberInput, Panel, Skeleton, Textarea } from '../ui';
import { PeoplePicker } from './PeoplePicker';
import { SaveNote } from './SaveNote';

type Read = { status: 'loading' } | { status: 'failed'; text: string } | { status: 'loaded'; view: PppAdminView };

const ELIGIBILITY: Array<{ value: PppForm['eligibility']; label: string }> = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'allowlist', label: 'Allow-list' },
  { value: 'group', label: 'Entra group' },
];

/**
 * Governance → Create my PPP: who may build one, the limits (plain names), holidays, the waiver, the label id,
 * leadership, department aliases and reminders (the allow-list and the leadership readers with the people picker) (GET/PUT /v1/admin/ppp), with a read-only "Runs in progress"
 * panel from GET /v1/admin/ppp/status that the deploy runbook reads before a gyro-svc deploy. Bridge-dependent switches
 * stay editable but say "needs The Bridge (not connected)" while the service reports that path off. The feature flag
 * itself is in the generic Feature flags table (`ppp-report`). Rebuilt on the kit (overhaul step 4): the same calls, the
 * same fields and words.
 */
export function PppCard() {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [form, setForm] = useState<PppForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  // True once a read has succeeded: a later failed refresh then keeps the form and its unsaved edits.
  const loadedOnce = useRef(false);

  const load = async (fresh: boolean) => {
    try {
      const view = await api.pppStatus();
      loadedOnce.current = true;
      setRead({ status: 'loaded', view });
      if (fresh || !form) setForm(pppForm(view.settings));
      // A read that succeeds retires an earlier read failure's note; a save's own note stays.
      setNote((current) => (current && !current.ok ? null : current));
    } catch (error) {
      if (loadedOnce.current) setNote({ ok: false, text: pppReadFailure(error) });
      else setRead({ status: 'failed', text: pppReadFailure(error) });
    }
  };

  const retry = async () => {
    setNote(null);
    setRead({ status: 'loading' });
    await load(true);
  };

  useEffect(() => {
    let live = true;
    api.pppStatus().then(
      (view) => {
        if (!live) return;
        loadedOnce.current = true;
        setRead({ status: 'loaded', view });
        setForm(pppForm(view.settings));
      },
      (error: unknown) => {
        if (live) setRead({ status: 'failed', text: pppReadFailure(error) });
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await load(false);
    } finally {
      setRefreshing(false);
    }
  };

  const save = async () => {
    if (read.status !== 'loaded' || !form) return;
    setNote(null);
    const problem = validatePpp(form);
    if (problem) {
      setNote({ ok: false, text: problem });
      return;
    }
    const patch = pppPatch(form, read.view.settings);
    if (Object.keys(patch).length === 0) {
      setNote({ ok: true, text: 'Nothing changed.' });
      return;
    }
    setSaving(true);
    try {
      const view = await api.savePpp(patch);
      setRead({ status: 'loaded', view });
      setForm(pppForm(view.settings));
      setNote({ ok: true, text: 'PPP settings saved. They apply from the next run, and the change is in the audit trail.' });
    } catch (error) {
      setNote({ ok: false, text: errorText(error, 'The PPP settings were not saved.') });
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof PppForm>(key: K, value: PppForm[K]) => setForm((current) => (current ? { ...current, [key]: value } : current));

  const field = (spec: PppField, view: PppAdminView, current: PppForm) => {
    const id = `ppp-${spec.key}`;
    const bridgeOff = spec.bridge ? !view.bridge[spec.bridge] : false;
    const hint = bridgeOff ? `${spec.hint} needs The Bridge (not connected)` : spec.hint;
    switch (spec.kind) {
      case 'radio':
        return (
          <fieldset key={spec.key} className="ui-fieldset ui-field">
            <legend className="ui-field-label">{spec.label}</legend>
            <div className="ui-actions">
              {ELIGIBILITY.map((option) => (
                <label key={option.value} className="ui-check">
                  <input type="radio" name="ppp-eligibility" value={option.value} checked={current.eligibility === option.value} onChange={() => set('eligibility', option.value)} />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <p className="ui-field-note">{hint}</p>
          </fieldset>
        );
      case 'people':
        return (
          <div key={spec.key} className="ui-field">
            <label className="ui-field-label" htmlFor={id}>{spec.label}</label>
            <PeoplePicker id={id} label={spec.label} value={String(current[spec.key])} max={spec.bounds?.max ?? 200} onChange={(next) => set(spec.key, next as never)} />
            <p className="ui-field-note">{hint}</p>
          </div>
        );
      case 'lines':
      case 'aliases':
        return (
          <Field key={spec.key} label={spec.label} help={hint}>
            <Textarea id={id} rows={3} aria-label={spec.label} value={String(current[spec.key])} onChange={(e) => set(spec.key, e.target.value as never)} />
          </Field>
        );
      case 'text':
        return (
          <Field key={spec.key} label={spec.label} help={hint}>
            <Input id={id} aria-label={spec.label} value={String(current[spec.key])} onChange={(e) => set(spec.key, e.target.value as never)} />
          </Field>
        );
      case 'number':
        return (
          <Field key={spec.key} label={spec.label} help={spec.bounds ? `${hint} ${spec.bounds.min}–${spec.bounds.max}.` : hint}>
            <NumberInput id={id} aria-label={spec.label} inputMode="decimal" className="ui-w-sm" value={String(current[spec.key])} onChange={(e) => set(spec.key, e.target.value as never)} />
          </Field>
        );
      case 'switch':
        return (
          <div key={spec.key} className="ui-field">
            <Checkbox aria-label={spec.label} checked={Boolean(current[spec.key])} onChange={(e) => set(spec.key, e.target.checked as never)} label={spec.label} />
            <p className="ui-field-note">{hint}</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Panel
      title={<span className="ui-title-icon"><Icon icon={FileText} size="md" />Create my PPP</span>}
      description="Who may build a PPP from the desktop app or the Office add-in, the limits, and the reminders"
      actions={read.status === 'loaded' ? <Button size="sm" icon={RefreshCw} busy={refreshing} onClick={() => void refresh()}>Refresh status</Button> : null}
      footer={<span>The feature itself is switched on with the <code>ppp-report</code> flag under Feature flags.</span>}
    >
      {read.status === 'failed' ? (
        <div className="ui-form">
          <p className="ui-note is-bad" role="alert"><span>{read.text}</span></p>
          <div className="ui-actions"><Button icon={RefreshCw} onClick={() => void retry()}>Retry</Button></div>
        </div>
      ) : read.status === 'loading' || !form ? (
        <div className="ui-form" aria-busy="true">
          <span className="ui-sr">Loading the PPP settings</span>
          <Skeleton size="md" width="280px" /><Skeleton size="lg" /><Skeleton size="lg" />
        </div>
      ) : (
        <div className="ui-form">
          <div className="ui-callout ui-callout--neutral" role="status" aria-label="PPP status">
            <div className="ui-callout-text">
              <p className="ui-callout-title">{runsInProgressText(read.view)}</p>
              <div className="ui-callout-body">
                <p className="ui-para">{pppStatusText(read.view)}</p>
                {bridgeLines(read.view).map((line) => <p key={line} className="ui-para">{line}</p>)}
              </div>
            </div>
          </div>
          {PPP_FIELDS.map((spec) => field(spec, read.view, form))}
          <div className="ui-actions">
            <Button variant="primary" icon={Check} busy={saving} onClick={() => void save()}>Save PPP settings</Button>
          </div>
          <SaveNote note={note} />
        </div>
      )}
    </Panel>
  );
}
